import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { addHours, format, parseISO, startOfHour } from "date-fns";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  makeStyles,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";

const defaultSendAt = () =>
  format(startOfHour(addHours(new Date(), 2)), INPUT_FORMAT);

const ScheduleModal = ({
  open,
  onClose,
  scheduleId,
  initialContact,
  initialSendAt,
}) => {
  const classes = useStyles();
  const [contact, setContact] = useState(null);
  const [body, setBody] = useState("");
  const [sendAt, setSendAt] = useState(defaultSendAt());
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!scheduleId) {
      setContact(initialContact || null);
      setBody("");
      setSendAt(
        initialSendAt ? format(initialSendAt, INPUT_FORMAT) : defaultSendAt()
      );
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/schedules/${scheduleId}`);
        setContact(data.contact);
        setBody(data.body);
        setSendAt(format(parseISO(data.sendAt), INPUT_FORMAT));
      } catch (err) {
        toastError(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheduleId, initialContact?.id]);

  useEffect(() => {
    if (!open) return undefined;
    setLoadingOptions(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: { searchParam: search, pageNumber: 1 },
        });
        setOptions(data.contacts.filter((c) => !c.isGroup));
      } catch (err) {
        toastError(err);
      }
      setLoadingOptions(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [search, open]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      contactId: contact?.id,
      body,
      sendAt: new Date(sendAt).toISOString(),
    };
    try {
      if (scheduleId) {
        await api.put(`/schedules/${scheduleId}`, payload);
      } else {
        await api.post("/schedules", payload);
      }
      toast.success(i18n.t("schedules.toasts.saved"));
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t(scheduleId ? "scheduleModal.title.edit" : "scheduleModal.title.add")}
      </DialogTitle>
      <form onSubmit={handleSave}>
        <DialogContent dividers>
          <Autocomplete
            className={classes.field}
            options={options}
            value={contact}
            loading={loadingOptions}
            disabled={!!initialContact && !scheduleId}
            getOptionLabel={(option) =>
              option.number ? `${option.name} (${option.number})` : option.name || ""
            }
            getOptionSelected={(option, value) => option.id === value.id}
            filterOptions={(x) => x}
            onChange={(e, value) => setContact(value)}
            onInputChange={(e, value, reason) => {
              if (reason === "input") setSearch(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n.t("scheduleModal.form.contact")}
                variant="outlined"
                required
              />
            )}
          />
          <TextField
            className={classes.field}
            label={i18n.t("scheduleModal.form.body")}
            helperText={i18n.t("scheduleModal.form.bodyHelp", { name: "{{name}}" })}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            variant="outlined"
            multiline
            minRows={4}
            fullWidth
            required
            inputProps={{ maxLength: 4096 }}
          />
          <TextField
            label={i18n.t("scheduleModal.form.sendAt")}
            type="datetime-local"
            value={sendAt}
            onChange={(e) => setSendAt(e.target.value)}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: format(new Date(), INPUT_FORMAT) }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving} variant="outlined">
            {i18n.t("scheduleModal.buttons.cancel")}
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={saving || !contact}
            className={classes.btnWrapper}
          >
            {i18n.t(scheduleId ? "scheduleModal.buttons.okEdit" : "scheduleModal.buttons.ok")}
            {saving && <CircularProgress size={24} className={classes.buttonProgress} />}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ScheduleModal;
